// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { type AskPunttHomeCopy } from "@mg/schemas/src/christo/askPunttHomeCopy";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { clientLocalDateTime, clientTimezone, localDateKey } from "./homeCopy";

import { ASK_V2_HOME_COPY_QUERY_KEY, getHomeCopy } from "../services/homeCopy";

export const ASK_V2_HOME_COPY_STALE_MS = 60 * 60 * 1000;

export type AskV2HomeCopyState = {
  /** Personalized lines, or null while loading and whenever christo fell back. */
  copy: AskPunttHomeCopy | null;
  pending: boolean;
};

/**
 * One request per calendar day and language: the key carries the local date
 * so a tab left open overnight greets the new morning, while navigating back
 * to home within the hour reuses the cached copy instead of paying the model.
 */
export function useAskV2HomeCopy(): AskV2HomeCopyState {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const timezone = clientTimezone();
  const localDateTime = clientLocalDateTime();

  const query = useQuery({
    queryKey: [
      ...ASK_V2_HOME_COPY_QUERY_KEY,
      localDateKey(localDateTime),
      locale,
    ],
    queryFn: () => getHomeCopy({ timezone, localDateTime, locale }),
    staleTime: ASK_V2_HOME_COPY_STALE_MS,
    gcTime: ASK_V2_HOME_COPY_STALE_MS,
    // why: the default copy is already on screen; a retry would only delay
    // the swap, and a failed personalization is not worth a second model call.
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    copy: query.data?.personalized ? query.data.copy : null,
    pending: query.isPending,
  };
}
